import React, { useState } from "react";
import { Alert, Box, Button, FormHelperText, FormLabel, InputAdornment, Slider, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { LoadingButton, Table, TableBody, TableCell, TableRow } from "ui";

import { useGetVersionBomQuery } from "fond/api";
import { useServiceLocationsCount } from "fond/hooks/useCostToServe";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { Project, WORKFLOW_KEYS } from "fond/types";
import { Currency, CurrencyDetails } from "fond/types/currency";
import { formatCurrency } from "fond/utils/currency";
import { useAppSelector } from "fond/utils/hooks";
import { formatNumber } from "fond/utils/number";
import { NumericInput, SliderValueLabelTooltip, useStackedNavigationContext } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";

import { BudgetSliderConfig, calculateBudgets, calculateEffectiveBudgets, calculateSliderStep, isBudgetConfigRestrictive } from "./helper";

import { ModeToggleButton, ModeToggleButtonGroup } from "./OptimizePanel.styles";

interface OptimizePanelProps {
  /**
   * The project currently being viewed / edited
   */
  project: Project;
  /**
   * Flag indicating if the editing is read only
   */
  isReadOnly?: boolean;
  /**
   * Optional currency for formatting slider labels
   */
  currency?: Currency;
}

interface OptimizeFormProps extends Pick<OptimizePanelProps, "isReadOnly" | "currency"> {
  versionId: string;
  bomTotal: number;
  serviceLocationCount: number;
  monetary: BudgetSliderConfig;
  costPerPassing: BudgetSliderConfig;
}

/**
 * Which budgets constrain the optimization.
 */
type BudgetMode = "monetary" | "costPerPassing" | "both";

interface BudgetControlProps extends Pick<OptimizePanelProps, "isReadOnly" | "currency"> {
  id: string;
  label: string;
  /**
   * Whether to render the label above the control; the accessible names remain either way.
   */
  showLabel: boolean;
  max: number;
  value: number;
  onChange(value: number): void;
  /**
   * Value cap implied by the other budget; slider and input edits are clamped to it.
   */
  limit?: number;
  /**
   * Explanation of where the limit comes from, prefixing the helper text shown while it applies.
   */
  limitReason?: string;
  /**
   * Slider position to highlight with a tick mark.
   */
  markValue?: number;
}

/**
 * Fraction of the slider range within which a dragged value snaps to the mark.
 */
const MARK_SNAP_RATIO = 0.02;

/**
 * Error shown when the selected budgets don't restrict the build below the full design.
 */
const NON_RESTRICTIVE_MESSAGES: Record<BudgetMode, string> = {
  monetary: "This budget doesn't restrict the current build scope. Lower the monetary budget below the current total cost.",
  costPerPassing: "This budget doesn't restrict the current build scope. Lower the cost per passing budget below the current average.",
  both: "These budgets don't restrict the current build scope. Lower the monetary budget below the current total cost, or the cost per passing budget below the current average.",
};

const BudgetControl: React.FC<BudgetControlProps> = ({
  id,
  label,
  showLabel,
  max,
  value,
  onChange,
  limit,
  limitReason,
  markValue,
  isReadOnly,
  currency = "USD",
}) => {
  const [inputValue, setInputValue] = useState(String(value));
  const [isEditing, setIsEditing] = useState(false);
  const maxValue = Math.min(max, limit ?? max);
  const step = calculateSliderStep(max);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const parsed = Number(event.target.value);
    if (event.target.value !== "" && !Number.isNaN(parsed)) {
      onChange(Math.min(Math.max(Math.round(parsed), 0), maxValue));
    }
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const dragged = newValue as number;
    // Single-unit deltas come from keyboard nudges; keep them exact so the step and mark
    // rounding cannot trap the thumb.
    if (Math.abs(dragged - value) <= 1) {
      onChange(Math.min(dragged, maxValue));
      return;
    }
    const snapped = markValue !== undefined && Math.abs(dragged - markValue) <= max * MARK_SNAP_RATIO ? markValue : Math.round(dragged / step) * step;
    onChange(Math.min(snapped, maxValue));
  };

  return (
    <>
      {showLabel && (
        <Box width="100%" display="flex" flexDirection="row" alignItems="center" mt={2} mb={1}>
          <FormLabel htmlFor={id}>{label}</FormLabel>
        </Box>
      )}
      <Box pl={2} display="flex" alignItems="center" gap={2} mt={showLabel ? 0 : 2}>
        <Slider
          aria-label={label}
          min={0}
          max={max}
          step={1}
          value={value}
          onChange={handleSliderChange}
          disabled={isReadOnly}
          valueLabelDisplay="auto"
          valueLabelFormat={(labelValue) => formatCurrency(labelValue, { currency })}
          marks={markValue !== undefined ? [{ value: markValue }] : false}
          track="normal"
          size="small"
          sx={{ flex: 1 }}
          slots={{
            valueLabel: SliderValueLabelTooltip,
          }}
        />
        <NumericInput
          id={id}
          value={isEditing ? inputValue : formatNumber(value)}
          onChange={handleInputChange}
          onFocus={() => {
            setInputValue(String(value));
            setIsEditing(true);
          }}
          onBlur={() => setIsEditing(false)}
          disabled={isReadOnly}
          size="small"
          sx={{ width: 130 }}
          InputProps={{ startAdornment: <InputAdornment position="start">{CurrencyDetails[currency].Symbol}</InputAdornment> }}
          inputProps={{ "aria-label": label, pattern: "[0-9.,]*" }}
        />
      </Box>
      {limit !== undefined && limit < max && (
        <FormHelperText sx={{ pl: 2 }}>
          {limitReason} the current maximum {label.toLowerCase()} is <b>{formatCurrency(limit, { currency })}</b>.
        </FormHelperText>
      )}
    </>
  );
};

const OptimizeForm: React.FC<OptimizeFormProps> = ({
  versionId,
  bomTotal,
  serviceLocationCount,
  monetary,
  costPerPassing,
  isReadOnly,
  currency = "USD",
}) => {
  const { goBack, params } = useStackedNavigationContext();
  const scenarioName = params.optimize?.scenarioName || "Untitled Scenario";
  const { runWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.COLLABORATION_CTS.CFO_ITERATION, refreshVersionDataOnFail: true });

  const [budgetMode, setBudgetMode] = useState<BudgetMode>("monetary");
  const [monetaryBudget, setMonetaryBudget] = useState(monetary.defaultValue);
  const [costPerPassingBudget, setCostPerPassingBudget] = useState(costPerPassing.defaultValue);
  const [isWaitingForWorkflowStart, setIsWaitingForWorkflowStart] = useState(false);

  const isMonetaryBudgetEnabled = budgetMode !== "costPerPassing";
  const isCostPerPassingEnabled = budgetMode !== "monetary";

  const effectiveBudgets = calculateEffectiveBudgets({
    monetary,
    costPerPassing,
    isMonetaryBudgetEnabled,
    isCostPerPassingBudgetEnabled: isCostPerPassingEnabled,
    monetaryBudget,
    costPerPassingBudget,
    serviceLocationCount,
  });
  const isRestrictive = isBudgetConfigRestrictive({
    bomTotal,
    averageCostPerPassing: costPerPassing.defaultValue,
    monetaryBudget: isMonetaryBudgetEnabled ? effectiveBudgets.monetary.value : null,
    costPerPassingBudget: isCostPerPassingEnabled ? effectiveBudgets.costPerPassing.value : null,
  });

  const handleOnConfirm = async () => {
    setIsWaitingForWorkflowStart(true);
    await runWorkflow?.({
      MonetaryBudget: isMonetaryBudgetEnabled ? effectiveBudgets.monetary.value : null,
      CostPerPassingBudget: isCostPerPassingEnabled ? effectiveBudgets.costPerPassing.value : null,
      TargetVersionName: scenarioName,
    });
    goBack();
  };

  return (
    <>
      <Typography variant="caption" component="p" lineHeight={1.5}>
        Set your financial objectives to optimize the build scope.
      </Typography>

      <Box my={2}>
        <Table size="small" variant="ag-grid-compact">
          <TableBody>
            <TableRow>
              <TableCell>Current total cost</TableCell>
              <TableCell align="right">
                <b>{formatCurrency(bomTotal, { currency })}</b>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Current average cost per passing</TableCell>
              <TableCell align="right">
                <b>{formatCurrency(costPerPassing.defaultValue, { currency })}</b>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Box mt={2}>
        <FormLabel id="budgetMode">Limit the build scope by</FormLabel>
        <ModeToggleButtonGroup
          exclusive
          size="small"
          value={budgetMode}
          onChange={(_, mode: BudgetMode | null) => {
            if (mode) setBudgetMode(mode);
          }}
          aria-labelledby="budgetMode"
          disabled={isReadOnly}
          sx={{ mt: 0.75 }}
        >
          <ModeToggleButton value="monetary">Monetary budget</ModeToggleButton>
          <ModeToggleButton value="costPerPassing">Cost per passing</ModeToggleButton>
          <ModeToggleButton value="both">Both</ModeToggleButton>
        </ModeToggleButtonGroup>
      </Box>

      {isMonetaryBudgetEnabled && (
        <BudgetControl
          id="monetaryBudget"
          label="Monetary budget"
          showLabel={budgetMode === "both"}
          max={monetary.max}
          value={effectiveBudgets.monetary.value}
          onChange={setMonetaryBudget}
          limit={effectiveBudgets.monetary.limit}
          limitReason="Based on your current cost per passing budget,"
          markValue={isCostPerPassingEnabled && effectiveBudgets.monetary.limit < monetary.max ? effectiveBudgets.monetary.limit : undefined}
          isReadOnly={isReadOnly}
          currency={currency}
        />
      )}

      {isCostPerPassingEnabled && (
        <BudgetControl
          id="costPerPassingBudget"
          label="Cost per passing budget"
          showLabel={budgetMode === "both"}
          max={isMonetaryBudgetEnabled ? costPerPassing.max : costPerPassing.defaultValue}
          value={effectiveBudgets.costPerPassing.value}
          onChange={setCostPerPassingBudget}
          limit={effectiveBudgets.costPerPassing.limit}
          limitReason="Because your monetary budget is at its maximum,"
          markValue={isMonetaryBudgetEnabled ? costPerPassing.defaultValue : undefined}
          isReadOnly={isReadOnly}
          currency={currency}
        />
      )}

      {!isRestrictive && (
        <Alert severity="error" variant="compact" sx={{ mt: 2 }}>
          {NON_RESTRICTIVE_MESSAGES[budgetMode]}
        </Alert>
      )}

      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={() => goBack()} disabled={isWaitingForWorkflowStart}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleOnConfirm}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          disabled={!isRestrictive || isReadOnly || !runWorkflow}
          loading={isWaitingForWorkflowStart}
        >
          Confirm and run new scenario
        </LoadingButton>
      </Box>
    </>
  );
};

/**
 * Panel for optimizing the build scope based on financial objectives.
 */
const OptimizePanel: React.FC<OptimizePanelProps> = ({ project, isReadOnly, currency }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: bom, isLoading: isLoadingBom } = useGetVersionBomQuery(versionId ?? skipToken);
  const serviceLocationCount = useServiceLocationsCount({ project, versionId });

  const { monetary, costPerPassing } = calculateBudgets(bom?.total ?? 0, serviceLocationCount);

  return (
    <Box data-testid="optimize-panel" px={2}>
      {!versionId || isLoadingBom || bom === undefined || costPerPassing === undefined || !serviceLocationCount ? (
        <SkeletonLoadingGrid spacing={2} items={[{ height: 40 }, { height: 80 }, { height: 80 }, { height: 38 }]} />
      ) : (
        <OptimizeForm
          versionId={versionId}
          bomTotal={bom.total}
          serviceLocationCount={serviceLocationCount}
          monetary={monetary}
          costPerPassing={costPerPassing}
          isReadOnly={isReadOnly}
          currency={currency}
        />
      )}
    </Box>
  );
};

export default OptimizePanel;
